import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import CustomModal from "../CustomModal/CustomModal";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Form } from "react-bootstrap";
import {
  subscriptionTypeConstants,
  translationConstants,
  subscriptionUserTypeEmun,
  freelanceStatusEnum,
  roles,
  Directions,
  calculationOperations,
} from "../../../constants";
import IMG_Info from "../../../assets/images/icon/info.png";
import { ToastifyService } from "../../../services";
import { allSharedComponents } from "../../../shared";
import {
  calculateVAT,
  dateToUTCWithTimezone,
  getUserPayload,
  precisionCalculation,
  roundToDecimalPlaces,
  serverFormatToMomentObject,
} from "../../../system";
import {
  instructorActions,
  profileActions,
  SpotActions,
  SubscriptionActions,
} from "../../../redux/actions";
import { sportNames } from "../../../modules/AdminPortal/Views/Spots/utilities";

const { GeneralLogo, BaseForm } = allSharedComponents;

const FreelanceAddonModal = (props) => {
  const userPayload = getUserPayload();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm();

  const body = { length: null };
  const currentSAR = useRef(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const profileData = useSelector((state) => state.ProfileReducer.profileData);
  const publicSpots = useSelector((state) => state.SpotsReducer?.publicSpots);
  const subscriptionPrices = useSelector(
    (state) => state.SubscriptionReducer.subscriptionPrices
  );
  const freelanceInstructorSportsDetail = useSelector(
    (state) => state.InstructorReducer.freelanceInstructorSportsDetail
  );
  const instructorSports = useSelector(
    (state) => state.InstructorReducer.instructorSports.data
  );
  const paymentHistoryStats = useSelector(
    (state) => state.PaymentReducer.paymentHistoryStats
  );

  const [checkboxState, setCheckboxState] = useState({
    [subscriptionTypeConstants.SAILING]: false,
    [subscriptionTypeConstants.KITE_SURFING]: false,
    [subscriptionTypeConstants.WIND_SURFING]: false,
    [subscriptionTypeConstants.WING_FOILING]: false,
  });

  const [freelanceData, setFreelanceData] = useState();

  const [isYearlyPricingModal, setYearlyPricingModal] = useState(false);
  const [isAddonPricingModal, setAddonPricingModal] = useState(false);

  const [selectedSpot, setSelectedSpot] = useState(null);
  const [selectedSpotId, setSelectedSpotId] = useState(null);

  const [pricing, setPricingBreakdown] = useState([]);
  const [addonPricing, setAddonPricingBreakdown] = useState([]);
  const [sportIds, setSportIds] = useState([]);
  const [vat, setVat] = useState(0);

  const handleCheckboxChange = (sportId) => {
    setCheckboxState((prevState) => {
      const isChecked = !prevState[sportId];
      const newState = { ...prevState, [sportId]: isChecked };

      // Update freelanceData based on checkbox state
      setFreelanceData((prevData) => {
        const newSportIds = isChecked
          ? [...(prevData?.sportIds || []), sportId]
          : (prevData?.sportIds || []).filter((id) => id !== sportId);

        // Remove duplicates from newSportIds
        const uniqueSportIds = [...new Set(newSportIds)];

        // Update the freelanceData state
        return { ...prevData, sportIds: uniqueSportIds };
      });

      // Handle SAR based on the new checkbox state
      handleSAR(sportId, isChecked);

      return newState;
    });
  };

  //Initial UseEffect
  useEffect(() => {
    if (publicSpots) {
      dispatch(
        SubscriptionActions.GetAllSubscriptionPrices({ isLoading: false })
      );
      dispatch(SpotActions.GetAllPublicSpots(body));
    }
    if (userPayload?.user?.role === roles.FreelanceInstructor) {
      dispatch(instructorActions.getInstructorsSports(userPayload?.user?.id));
    }
    // eslint-disable-next-line
  }, []);

  //set Freelance Sport Detail from Freelance Data
  useEffect(() => {
    setFreelanceData(freelanceInstructorSportsDetail);
    //eslint-disable-next-line
  }, [freelanceInstructorSportsDetail]);

  //Fetch VAT from Subscriptions
  useEffect(() => {
    const data = subscriptionPrices.find(
      (x) => x.subcriptionType === subscriptionTypeConstants.VAT
    );
    if (data && data.price) setVat(data.price);
    // eslint-disable-next-line
  }, [subscriptionPrices]);

  const handleSAR = (constant, isChecked) => {
    const data = subscriptionPrices.find(
      (x) =>
        x.subcriptionType === constant &&
        x.userType === subscriptionUserTypeEmun.FREELANCE
    );
    if (data) {
      const price = data.price;
      if (isChecked) {
        currentSAR.current += price;
        setPricingBreakdown((prev) => [...prev, { sport: constant, price }]);
        setSportIds((prev) => [...prev, constant]);
      } else {
        currentSAR.current -= price;
        setPricingBreakdown((prev) =>
          prev.filter((item) => item.sport !== constant)
        );
        setSportIds((prev) => prev.filter((item) => item !== constant));
      }
    }
  };

  const onSubmit = (e) => {
    if (sportIds.length <= 0) {
      ToastifyService.showError(t(translationConstants.SELECT_ONE_SPORT));
    } else {
      dispatch(
        instructorActions.updateFreelancerSportsDetail(
          {
            spotId: e.primaryLocation,
            sportIds: sportIds,
            id: freelanceData?.id,
            fee: addonPricing?.totalAddonCost,
          },
          () => {
            props.setShow(false);
            dispatch(profileActions.getProfileData(userPayload?.user?.id));
            dispatch(
              instructorActions.getFreelancerSportsDetail(
                userPayload?.instructorId
              )
            );
          }
        )
      );
    }
  };

  const checkboxHandler = (sportId) => {
    if (sportId) {
      handleCheckboxChange(sportId);
    } else {
      freelanceData?.sportIds?.forEach((element) =>
        handleCheckboxChange(element)
      );
    }
  };

  const resetHandler = () => {
    const availableSports = selectedSpot?.sportIds?.filter((sportId) =>
      instructorSports?.includes(sportId)
    );
    availableSports?.forEach((sportId) => setValue(sportNames[sportId], false));
    setPricingBreakdown([]);
    setSportIds([]);
    currentSAR.current = 0;

    setCheckboxState({
      [subscriptionTypeConstants.SAILING]: false,
      [subscriptionTypeConstants.KITE_SURFING]: false,
      [subscriptionTypeConstants.WIND_SURFING]: false,
      [subscriptionTypeConstants.WING_FOILING]: false,
    });
  };

  useEffect(() => {
    if (selectedSpot) {
      resetHandler();
      reset();
    }
    // eslint-disable-next-line
  }, [selectedSpot, reset]);

  const handleSpotChange = (event) => {
    const selectedSpotId = event.target.value;
    setSelectedSpotId(selectedSpotId);
    const spot = publicSpots.find((spot) => spot.id === selectedSpotId);
    if (spot) {
      // Check if the selected spot's sportIds cover all the required instructorSports and freelanceData.sportIds
      const requiredSports = new Set([...(freelanceData?.sportIds || [])]);

      const missingSports = [...requiredSports].some(
        (sportId) => !spot.sportIds.includes(sportId)
      );

      if (missingSports) {
        ToastifyService.showError(
          t(translationConstants.SELECTED_SPOT_LACKS_YOUR_SPORTS)
        );
        setSelectedSpotId(""); // Reset dropdown to default value
        setSelectedSpot(null); // Clear selected spot
      } else {
        setSelectedSpot(spot);
      }
    }
  };

  const singleSportAddonCal = (price, daysDiff) => {
    // Calculate the cost per day
    const amountPerDay = price / 365;

    // Calculate the total addon pricing
    const totalAmount = amountPerDay * daysDiff;

    return roundToDecimalPlaces(totalAmount, 2)?.toFixed(2);
  };

  const addonPaymentCalculations = () => {
    // Extract the sport IDs from freelanceData
    const existingSports = freelanceInstructorSportsDetail?.sportIds || [];

    // Freelance next billing date
    const nextBillingDueDate = paymentHistoryStats?.nextDueDateString;

    const nextDueDate =
      serverFormatToMomentObject(nextBillingDueDate).startOf("day");
    const currentDate = dateToUTCWithTimezone().startOf("day");
    const duration = moment.duration(nextDueDate.diff(currentDate));

    // Difference between current and next billing date
    const daysDiffInFloat = duration.asDays();
    const daysDiff = Math.ceil(daysDiffInFloat);

    // Filter the pricing array to exclude sports already in freelanceData
    const filteredPricing = pricing.filter(
      (item) => !existingSports.includes(item.sport)
    );

    // Calculate the total cost of the addon pricing
    const yearlyAmount = filteredPricing.reduce(
      (acc, item) =>
        precisionCalculation(acc, item.price, calculationOperations.addition),
      0
    );

    const sportIdsArray = filteredPricing.map((item) => {
      const updatedPrice = singleSportAddonCal(item.price, daysDiff);
      return { ...item, price: updatedPrice };
    });

    // Calculate the total addon pricing
    const totalAmount = sportIdsArray.reduce(
      (acc, item) =>
        precisionCalculation(acc, item.price, calculationOperations.addition),
      0
    );

    // Calculate the VAT on the addon pricing
    const vatPrice = calculateVAT(vat, totalAmount);

    // Calculate the total cost including VAT
    const totalAddonCost = precisionCalculation(
      vatPrice,
      totalAmount,
      calculationOperations.addition
    );

    const pricingBreakdown = {
      sportIds: sportIdsArray,
      yearlyAmount: yearlyAmount,
      totalAmount: totalAmount,
      vat: vatPrice,
      totalAddonCost: totalAddonCost,
    };

    // Update the addonPricing state
    setAddonPricingBreakdown(pricingBreakdown);
  };

  useEffect(() => {
    addonPaymentCalculations();
    //eslint-disable-next-line
  }, [pricing, freelanceData]);

  useEffect(() => {
    if (!selectedSpot) {
      const selectedFreelanceSpotId =
        freelanceInstructorSportsDetail?.spotId || "";
      setSelectedSpotId(selectedFreelanceSpotId);
      const spot = publicSpots?.find((spot) => spot.id === selectedSpotId);
      setSelectedSpot(spot);
      setFreelanceData(freelanceInstructorSportsDetail);
    }
    // eslint-disable-next-line
  }, [selectedSpot, publicSpots, freelanceInstructorSportsDetail]);

  useEffect(() => {
    if (selectedSpot) {
      checkboxHandler(null);
    }
    // eslint-disable-next-line
  }, [selectedSpot]);

  const renderCurrentCheckboxes = () => {
    if (!selectedSpot) {
      return <p>{t(translationConstants.PLEASE_SELECT_A_PRIMARY_LOCATION)}</p>;
    }

    if (!instructorSports) {
      return null;
    }

    const availableSports = selectedSpot?.sportIds?.filter(
      (sportId) =>
        instructorSports.includes(sportId) &&
        freelanceData?.sportIds?.includes(sportId)
    );

    if (availableSports?.length === 0) {
      return <p>{t(translationConstants.NO_COMMON_SPORTS_FOUND)}</p>;
    }

    return availableSports?.map((sportId) => (
      <div className="col-6 my-2" key={sportId}>
        <Form.Group controlId={`formBasicCheckbox${sportId}`}>
          <Form.Check
            className="offer"
            type="checkbox"
            label={
              <p>
                {t(translationConstants[sportNames[sportId].toLowerCase()])}
              </p>
            }
            {...register(sportNames[sportId])}
            onChange={() => handleCheckboxChange(sportId)}
            checked={checkboxState[sportId]}
          />
          {errors[sportNames[sportId]] && (
            <p className="errorText">{errors[sportNames[sportId]].message}</p>
          )}
        </Form.Group>
      </div>
    ));
  };

  const renderChooseCheckboxes = () => {
    if (!selectedSpot) {
      return <p>{t(translationConstants.PLEASE_SELECT_A_PRIMARY_LOCATION)}</p>;
    }

    if (!instructorSports) {
      return null;
    }

    const availableSports = selectedSpot?.sportIds?.filter(
      (sportId) =>
        instructorSports.includes(sportId) &&
        !freelanceData?.sportIds?.includes(sportId)
    );

    if (availableSports.length === 0) {
      return <p>{t(translationConstants.NO_COMMON_SPORTS_FOUND)}</p>;
    }

    return availableSports.map((sportId) => (
      <div className="col-6 my-2" key={sportId}>
        <Form.Group controlId={`formBasicCheckbox${sportId}`}>
          <Form.Check
            className="offer"
            type="checkbox"
            label={
              <p>
                {t(translationConstants[sportNames[sportId].toLowerCase()])}
              </p>
            }
            {...register(sportNames[sportId])}
            onChange={() => handleCheckboxChange(sportId)}
            checked={checkboxState[sportId]}
          />
          {errors[sportNames[sportId]] && (
            <p className="errorText">{errors[sportNames[sportId]].message}</p>
          )}
        </Form.Group>
      </div>
    ));
  };

  const addonReset = (data) => {
    setSelectedSpot(null);
    setSelectedSpotId(null);
    resetHandler();
    reset();
    props.setShow(data);
  };

  const getTotalPriceYearly = useMemo(() => {
    return precisionCalculation(
      calculateVAT(vat, currentSAR.current),
      currentSAR.current,
      calculationOperations.addition
    );
    //eslint-disable-next-line
  }, [vat, currentSAR.current]);

  return (
    <>
      <div>
        <CustomModal
          size="sm"
          scrollable={true}
          setShow={addonReset}
          show={props.isShow}
          closeBtnClasses={"login-modal-close-btn"}
        >
          <BaseForm>
            <GeneralLogo />
            <section
              id="content"
              className="freelance-instructor d-flex flex-column align-content-start"
            >
              <p className="heading text-center w-100">
                {t(translationConstants.UPDATE_FREELANCE_INSTRUCTOR)}
              </p>
              <section id="form">
                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="col-10">
                    <Form.Group className="form-group">
                      <Form.Label className="label">
                        {t(
                          translationConstants.SELECT_PRIMARY_TEACHING_LOCATION
                        )}
                      </Form.Label>
                      <Form.Select
                        className="custom-select-icon"
                        {...register("primaryLocation", {
                          required: t(
                            translationConstants.PRIMARY_LOCATION_IS_REQUIRED
                          ),
                        })}
                        value={selectedSpotId}
                        onChange={handleSpotChange}
                      >
                        <option value="">
                          {t(translationConstants.SELECT_PRIMARY_LOCATION)}
                        </option>
                        {publicSpots?.map((spot) => (
                          <option key={spot?.id} value={spot?.id}>
                            {spot?.name}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.primaryLocation && (
                        <p className="errorText">
                          {errors.primaryLocation.message}
                        </p>
                      )}
                    </Form.Group>
                  </div>
                  <div className="align-items-center registrationCheckBoxs">
                    <p className="choose-sport fw-bolder">
                      {t(translationConstants.CURRENT_SPORTS)}:
                    </p>
                    <div className="">
                      <div className="row checkbox">
                        <div
                          id="renderCurrentCheckboxes"
                          className="d-flex flex-wrap justify-content-between col-md-12 px-0"
                        >
                          {renderCurrentCheckboxes()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="align-items-center registrationCheckBoxs">
                    <p className="choose-sport fw-bolder">
                      {t(translationConstants.CHOOSE_SPORTS_YOU_OFFER)}:
                    </p>
                    <div className="">
                      <div className="row checkbox">
                        <div
                          id="renderChooseCheckboxes"
                          className="d-flex flex-wrap justify-content-between col-md-12 px-0"
                        >
                          {renderChooseCheckboxes()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="registrationCheckBoxs">
                    <div className="d-flex px-0 justify-content-between">
                      <p className="total-fee fw-bolder">
                        {t(translationConstants.TOTAL_FEE)}: (
                        {t(translationConstants.YEARLY)})
                      </p>
                      <p className="total-fee">
                        {translationConstants.SAR} {getTotalPriceYearly}
                        <img
                          alt="info-icon"
                          src={IMG_Info}
                          className="info-icon"
                          onClick={() => setYearlyPricingModal(true)}
                        />
                      </p>
                    </div>
                  </div>
                  <div className="">
                    <p className="sport-selected">
                      {t(translationConstants.FEE_WILL_BE_CALCULATED)}
                    </p>
                  </div>
                  {!(
                    profileData?.freelanceStatus ===
                      freelanceStatusEnum.PaymentRejected ||
                    profileData?.freelanceStatus ===
                      freelanceStatusEnum.Restricted
                  ) && (
                    <>
                      <div className="pt-2">
                        <div className="d-flex px-0 justify-content-between">
                          <p className="total-fee fw-bolder">
                            {t(translationConstants.PAYABLE_NOW)}:
                          </p>
                          <p className="total-fee">
                            {translationConstants.SAR}{" "}
                            {addonPricing?.totalAddonCost}
                            <img
                              alt="info-icon"
                              src={IMG_Info}
                              className="info-icon"
                              onClick={() => setAddonPricingModal(true)}
                            />
                          </p>
                        </div>
                      </div>
                      <div className="">
                        <p className="sport-selected">
                          {t(
                            translationConstants.AUTO_PAYMENT_FOR_NEW_SPORTS_AND_REMAINING_DURATION
                          )}
                          .
                        </p>
                      </div>
                    </>
                  )}
                  <div className="mt-3">
                    <div className="d-flex justify-content-center align-items-center">
                      <Button className="Submit-button mt-1" type="submit">
                        {t(translationConstants.Update)}
                      </Button>
                    </div>
                  </div>
                </Form>
              </section>
            </section>
          </BaseForm>
        </CustomModal>

        <CustomModal
          size={"sm"}
          scrollable={true}
          setShow={setYearlyPricingModal}
          show={isYearlyPricingModal}
          customClass={"freelance-instructor-model-sm"}
          closeBtnClasses={"login-modal-close-btn"}
          closeOnOutsideClick={true}
        >
          <div className="freelance-instructor-model-sm">
            <p className="pricing-heading mt-3">
              {t(translationConstants.PRICING_BREAKDOWN)}
            </p>
            <hr />
            <div className="d-flex justify-content-between mt-4">
              <p className="subscritption">
                {t(translationConstants.SUBSCRITION_FEE)}
                <span>({t(translationConstants.YEAR)})</span>
              </p>
              <span className="sar">({translationConstants.SAR})</span>
            </div>
            {pricing?.map((data) => {
              return (
                <>
                  <div className="d-flex justify-content-between mt-3">
                    <p>
                      {data.sport === subscriptionTypeConstants.SAILING
                        ? t(translationConstants.sailing)
                        : data.sport === subscriptionTypeConstants.KITE_SURFING
                        ? t(translationConstants.kitesurfing)
                        : data.sport === subscriptionTypeConstants.WING_FOILING
                        ? t(translationConstants.wingfoiling)
                        : data.sport === subscriptionTypeConstants.WIND_SURFING
                        ? t(translationConstants.windsurfing)
                        : ""}
                    </p>
                    <p>{data.price}</p>
                  </div>
                  <hr />
                </>
              );
            })}
            <div className="mt-4">
              <div className="d-flex justify-content-between w-100">
                <div>
                  <p className="footer-text">
                    {t(translationConstants.SUB_TOTAL)}
                  </p>
                </div>
                <div>
                  <p className="footer-text">{currentSAR.current}</p>
                </div>
              </div>
              <div className="d-flex justify-content-between w-100">
                <div>
                  <p className="footer-text">
                    {t(translationConstants.VAT)}{" "}
                    <span dir={Directions.ltr}>({vat}%)</span>
                  </p>
                </div>
                <div>
                  <p className="footer-text">
                    {calculateVAT(vat, currentSAR.current)}
                  </p>
                </div>
              </div>
              <div className="d-flex justify-content-between w-100">
                <div>
                  <p className="footer-text">
                    {t(translationConstants.TOTAL_FEE)}
                  </p>
                </div>
                <div>
                  <p className="footer-text">{getTotalPriceYearly}</p>
                </div>
              </div>
            </div>
            <div className="text-center mt-3 font-8">
              {`${t(translationConstants.VAT)}: ${
                process.env.REACT_APP_VAT_NO
              }`}
            </div>
          </div>
        </CustomModal>

        <CustomModal
          size={"sm"}
          scrollable={true}
          setShow={setAddonPricingModal}
          show={isAddonPricingModal}
          customClass={"freelance-instructor-model-sm"}
          closeBtnClasses={"login-modal-close-btn"}
          closeOnOutsideClick={true}
        >
          <div className="freelance-instructor-model-sm">
            <p className="pricing-heading mt-3">
              {t(translationConstants.PRICING_BREAKDOWN)}
            </p>
            <hr />
            <div className="d-flex justify-content-between mt-4">
              <p className="subscritption">
                {t(translationConstants.PAYABLE_AMOUNT)}
              </p>
              <span className="sar">({translationConstants.SAR})</span>
            </div>
            {addonPricing?.sportIds?.map((data) => {
              return (
                <>
                  <div className="d-flex justify-content-between mt-3">
                    <p>
                      {data.sport === subscriptionTypeConstants.SAILING
                        ? t(translationConstants.sailing)
                        : data.sport === subscriptionTypeConstants.KITE_SURFING
                        ? t(translationConstants.kitesurfing)
                        : data.sport === subscriptionTypeConstants.WING_FOILING
                        ? t(translationConstants.wingfoiling)
                        : data.sport === subscriptionTypeConstants.WIND_SURFING
                        ? t(translationConstants.windsurfing)
                        : ""}
                    </p>
                    <p>{data.price}</p>
                  </div>
                  <hr />
                </>
              );
            })}
            <div className="mt-4">
              <div className="d-flex justify-content-between w-100">
                <div>
                  <p className="footer-text">
                    {t(translationConstants.SUB_TOTAL)}
                  </p>
                </div>
                <div>
                  <p className="footer-text">{addonPricing?.totalAmount}</p>
                </div>
              </div>
              <div className="d-flex justify-content-between w-100">
                <div>
                  <p className="footer-text">
                    {t(translationConstants.VAT)}{" "}
                    <span dir={Directions.ltr}>({vat}%)</span>
                  </p>
                </div>
                <div>
                  <p className="footer-text">{addonPricing?.vat}</p>
                </div>
              </div>
              <div className="d-flex justify-content-between w-100">
                <div>
                  <p className="footer-text">
                    {t(translationConstants.TOTAL_FEE)}
                  </p>
                </div>
                <div>
                  <p className="footer-text">{addonPricing?.totalAddonCost}</p>
                </div>
              </div>
            </div>
            <div className="text-center mt-3 font-8">
              {`${t(translationConstants.VAT)}: ${
                process.env.REACT_APP_VAT_NO
              }`}
            </div>
          </div>
        </CustomModal>
      </div>
    </>
  );
};

export default FreelanceAddonModal;
